"use client";

import Link from "next/link";
import { Facebook, Instagram, Music2 } from "lucide-react";
import logoRomano from "@/public/assets/romano-logo.png";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { useBrandStore } from "@/stores/brand";
import XIcon from "@/components/ui/icons/x";

import { toAddressLines } from "@/lib/address";

type SocialPlatformKey = "facebook" | "x" | "instagram" | "tiktok";

function detectSocialPlatformKey(options: { slug?: string | null; url?: string | null }): SocialPlatformKey | null {
  const slug = options.slug?.toLowerCase() ?? "";
  const url = options.url?.toLowerCase() ?? "";

  if (slug.includes("facebook") || url.includes("facebook.com")) return "facebook";
  if (slug.includes("instagram") || url.includes("instagram.com")) return "instagram";
  if (slug.includes("tiktok") || url.includes("tiktok.com")) return "tiktok";
  if (slug === "x" || slug.includes("twitter") || url.includes("x.com") || url.includes("twitter.com")) return "x";

  return null;
}

export function SiteFooter() {
  const { brand, isLoading } = useBrandStore();

  if (isLoading && !brand) {
    return (
      <footer id="footer" className="mt-14 bg-muted">
        <div className="mx-auto w-full px-5 py-8">
          <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-stretch animate-pulse">
            <div className="flex h-full flex-col gap-6">
              <div className="h-10 w-44 rounded-xl bg-muted-foreground/20" />

              <div className="max-w-sm space-y-2">
                <div className="h-3 w-full rounded-lg bg-muted-foreground/20" />
                <div className="h-3 w-[85%] rounded-lg bg-muted-foreground/20" />
              </div>

              <div className="mt-auto flex items-center gap-5">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-5 w-5 rounded-full bg-muted-foreground/20" />
                ))}
              </div>
            </div>

            <div className="flex h-full flex-col items-center gap-4 md:items-end md:gap-3">
              <div className="flex w-full flex-wrap justify-center gap-x-6 gap-y-3 md:justify-end md:gap-7">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-4 w-24 rounded-lg bg-muted-foreground/20" />
                ))}
              </div>

              <div className="mt-auto flex flex-col gap-3">
                <div className="flex flex-col items-center gap-2 text-center md:items-end md:text-right">
                  <div className="h-3 w-40 rounded-lg bg-muted-foreground/20" />
                  <div className="h-3 w-56 rounded-lg bg-muted-foreground/20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-foreground px-4 py-4 text-center text-[clamp(11px,0.95vw,13px)] text-background">
          <div className="mx-auto h-4 w-72 max-w-full rounded-lg bg-white/15" />
        </div>
      </footer>
    );
  }

  const phoneText = brand?.attributes?.contact_info?.phone ?? null;
  const phoneHref = phoneText ? phoneText.replace(/[^\d+]/g, "") : null;
  const emailText = brand?.attributes?.contact_info?.email ?? null;
  const addressLines = toAddressLines(brand?.attributes?.contact_info?.address);
  const addressFallback = [
    "Integrated Boutique Office, Bluegreen Office Tower, Jl. Outer Ring",
    "Road No.88 7th Floor",
  ];
  const addressTextLines = addressLines.length ? addressLines : addressFallback;
  const socialLinks = brand?.attributes?.social_links ?? [];
  const socialItems = socialLinks
    .map((item) => {
      const href = item?.url ?? null;
      if (!href) return null;
      const platformKey = detectSocialPlatformKey({
        slug: item?.social_platform?.slug,
        url: href,
      });
      if (!platformKey) return null;

      const icon =
        platformKey === "facebook"
          ? Facebook
          : platformKey === "instagram"
            ? Instagram
            : platformKey === "tiktok"
              ? Music2
              : XIcon;

      const label =
        platformKey === "facebook"
          ? "Facebook"
          : platformKey === "instagram"
            ? "Instagram"
            : platformKey === "tiktok"
              ? "TikTok"
              : "X";

      return { key: platformKey, href, label, Icon: icon };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .sort((a, b) => {
      const order: Record<SocialPlatformKey, number> = {
        facebook: 1,
        x: 2,
        instagram: 3,
        tiktok: 4,
      };
      return order[a.key] - order[b.key];
    });

  return (
    <footer id="footer" className="mt-14 bg-muted">
      <div className="mx-auto w-full px-5 py-8">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-stretch">
          <div className="flex h-full flex-col gap-6">
            <Link
              href="/"
              className="flex flex-col items-start gap-1 text-[clamp(12px,1vw,14px)] font-semibold tracking-[0.28em]"
              aria-label="Romano Home"
            >
              <Image
                src={logoRomano}
                alt="Romano Logo"
                className="h-[clamp(2.75rem,6vw,4rem)] w-auto brightness-0"
                priority
              />
            </Link>

            <p className="max-w-sm text-xs leading-5 text-muted-foreground">
              {addressTextLines.map((line, index) => (
                <span key={index}>
                  {line}
                  {index < addressTextLines.length - 1 ? <br /> : null}
                </span>
              ))}
            </p>

            <div className="mt-auto flex items-center gap-5 text-muted-foreground">
              {socialItems.length ? (
                socialItems.map((item) => (
                  <Button
                    key={item.key}
                    asChild
                    size="icon"
                    variant="ghost"
                    className="h-auto w-auto rounded-none p-0 hover:bg-transparent hover:text-foreground"
                    aria-label={item.label}
                  >
                    <Link href={item.href} target="_blank" rel="noreferrer noopener" prefetch={false}>
                      <item.Icon className="h-5 w-5" />
                    </Link>
                  </Button>
                ))
              ) : (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-auto w-auto rounded-none p-0 hover:bg-transparent hover:text-foreground"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-auto w-auto rounded-none p-0 hover:bg-transparent hover:text-foreground"
                    aria-label="X"
                  >
                    <XIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-auto w-auto rounded-none p-0 hover:bg-transparent hover:text-foreground"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-auto w-auto rounded-none p-0 hover:bg-transparent hover:text-foreground"
                    aria-label="TikTok"
                  >
                    <Music2 className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="flex h-full flex-col items-center gap-4 md:items-end md:gap-3">
            <nav className="flex w-full flex-wrap justify-center gap-x-6 gap-y-3 whitespace-normal md:flex-nowrap md:justify-end md:gap-7 md:overflow-x-auto md:whitespace-nowrap md:[-ms-overflow-style:none] md:[scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden">
              <Button
                asChild
                variant="link"
                className="h-auto shrink-0 p-0 text-[clamp(11px,0.95vw,13px)] font-medium tracking-[0.18em] text-muted-foreground no-underline hover:text-foreground hover:no-underline"
              >
                <Link href="/products">PRODUCTS</Link>
              </Button>
              <Button
                asChild
                variant="link"
                className="h-auto shrink-0 p-0 text-[clamp(11px,0.95vw,13px)] font-medium tracking-[0.18em] text-muted-foreground no-underline hover:text-foreground hover:no-underline"
              >
                <Link href="/about-romano">ABOUT ROMANO</Link>
              </Button>
              <Button
                asChild
                variant="link"
                className="h-auto shrink-0 p-0 text-[clamp(11px,0.95vw,13px)] font-medium tracking-[0.18em] text-muted-foreground no-underline hover:text-foreground hover:no-underline"
              >
                <Link href="/articles">NEWS & ARTICLES</Link>
              </Button>
              <Button
                asChild
                variant="link"
                className="h-auto shrink-0 p-0 text-[clamp(11px,0.95vw,13px)] font-medium tracking-[0.18em] text-muted-foreground no-underline hover:text-foreground hover:no-underline"
              >
                <Link href="/buy-now">BUY NOW</Link>
              </Button>
            </nav>

            <div className="mt-auto flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground md:flex-row md:flex-wrap md:items-center md:justify-end md:gap-x-4 md:gap-y-2 md:text-left">
                {phoneText && phoneHref ? (
                  <Link href={`tel:${phoneHref}`} className="hover:text-foreground">
                    {phoneText}
                  </Link>
                ) : (
                  <span className="text-muted-foreground/80">0812-3456-7890</span>
                )}
                <span className="hidden text-muted-foreground/60 md:inline">|</span>
                {emailText ? (
                  <Link href={`mailto:${emailText}`} className="hover:text-foreground">
                    {emailText}
                  </Link>
                ) : (
                  <span className="text-muted-foreground/80">consumercare@wipro-unza.co.id</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-foreground px-4 py-4 text-center text-[clamp(11px,0.95vw,13px)] text-background">
        Copyright {new Date().getFullYear()}, ROMANO, All Right Reserved.
      </div>
    </footer>
  );
}
